import React from 'react';
import { atom, useAtom, useSetAtom } from 'jotai';
import { abortableAtom, atomFamily, loadable } from 'jotai/utils';
import { useActiveProjectId, useServiceAuth } from '../../hooks';
import { ServiceAuth } from '../../hooks/useService/useService';
import FhirSearchService from '../../services/FhirSearchService';
import { DOCUMENT_EXTRACTION_V2_PROJECTION_IDENTIFIER_SYSTEM } from '../../services/MedicalResultsService';
import { REVIEWABLE_RESOURCE_TYPE_LIST } from '../../util/extractedResourceReview';
import {
  DOCUMENT_EXTRACTION_V2_APPROVED_AT_EXTENSION_URL,
  DOCUMENT_EXTRACTION_V2_APPROVED_BY_EXTENSION_URL,
  DOCUMENT_EXTRACTION_V2_REVIEW_STATUS_EXTENSION_URL,
} from '../SubjectViewer/Modules/Measurements/Graphs/Ranges/ExtractionFeedbackActions';

const fhirSearchService = new FhirSearchService();

type MaybeSource<T> = T | (() => T);

const resolveSource = <T>(input: MaybeSource<T>): T =>
  typeof input === 'function' ? (input as () => T)() : input;

type AsyncOptions = {
  immediate?: boolean;
};

export type MemberExtractionReviewCounts = {
  toApprove: number;
  toUnapprove: number;
};

export type MemberExtractionReviewCountsResult = {
  data: MemberExtractionReviewCounts | undefined;
  error: unknown;
  isFetching: boolean;
  isIdle: boolean;
  execute: () => void;
};

type CountState =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'hasError'; error: unknown }
  | { state: 'hasData'; data: MemberExtractionReviewCounts | undefined };

type ReviewCountAggregations = {
  extractions?: {
    doc_count?: number;
    approved?: { doc_count?: number };
  };
};

type ReviewCountResponse = {
  aggregations?: ReviewCountAggregations;
};

const pascalToSnakeCase = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2')
    .toLowerCase();

// Clinical indices store `extension` as an object, not a nested type. A nested
// query against that path is rejected by Elasticsearch.
const approvedExtractionQuery = {
  bool: {
    should: [
      {
        bool: {
          must: [
            {
              term: {
                'extension.url.keyword':
                  DOCUMENT_EXTRACTION_V2_REVIEW_STATUS_EXTENSION_URL,
              },
            },
            {
              term: {
                'extension.valueString.keyword': 'approved',
              },
            },
          ],
        },
      },
      {
        term: {
          'extension.url.keyword':
            DOCUMENT_EXTRACTION_V2_APPROVED_BY_EXTENSION_URL,
        },
      },
      {
        term: {
          'extension.url.keyword':
            DOCUMENT_EXTRACTION_V2_APPROVED_AT_EXTENSION_URL,
        },
      },
    ],
    minimum_should_match: 1,
  },
};

export const buildMemberExtractionReviewCountQuery = () => ({
  type: 'select',
  columns: [
    {
      type: 'elasticsearch',
      aggregations: {
        extractions: {
          filter: { match_all: {} },
          aggregations: {
            approved: {
              filter: approvedExtractionQuery,
            },
          },
        },
      },
    },
  ],
  limit: [
    { type: 'number', value: 0 },
    { type: 'number', value: 0 },
  ],
  from: REVIEWABLE_RESOURCE_TYPE_LIST.map(resourceType => ({
    table: pascalToSnakeCase(resourceType),
  })),
  where: {
    type: 'elasticsearch',
    query: {
      term: {
        'identifier.system.keyword':
          DOCUMENT_EXTRACTION_V2_PROJECTION_IDENTIFIER_SYSTEM,
      },
    },
  },
});

export const toReviewCounts = (
  aggregations: ReviewCountAggregations | undefined,
): MemberExtractionReviewCounts => {
  const total = aggregations?.extractions?.doc_count ?? 0;
  const toUnapprove = Math.min(
    aggregations?.extractions?.approved?.doc_count ?? 0,
    total,
  );

  return {
    toApprove: total - toUnapprove,
    toUnapprove,
  };
};

const searchAuthAtom = atom<ServiceAuth | undefined>(undefined);
const refreshFamily = atomFamily((_key: string) => atom(0));

const countKey = (projectId: string, patientId: string) =>
  `${projectId}\u0000${patientId}`;

const parseCountKey = (key: string) => {
  const [projectId, patientId] = key.split('\u0000');
  return { projectId, patientId };
};

const countsFamily = atomFamily((key: string) => {
  const sourceAtom = abortableAtom(async get => {
    get(refreshFamily(key));
    const auth = get(searchAuthAtom);
    const { projectId, patientId } = parseCountKey(key);
    if (!auth?.account || !auth.authToken || !projectId || !patientId) {
      return undefined;
    }

    const response = await fhirSearchService.searchPatient<ReviewCountResponse>(
      auth,
    )(
      projectId,
      patientId,
      buildMemberExtractionReviewCountQuery(),
      undefined,
      { queryId: 'member-extraction-review-counts' },
    );

    return toReviewCounts(response.data?.aggregations);
  });
  const loadableAtom = loadable(sourceAtom);

  return atom(
    (get): CountState => {
      if (!get(searchAuthAtom)?.authToken) {
        return { state: 'idle' };
      }

      const value = get(loadableAtom);
      if (value.state === 'loading') {
        return { state: 'loading' };
      }
      if (value.state === 'hasError') {
        return { state: 'hasError', error: value.error };
      }
      if (value.data === undefined) {
        return { state: 'idle' };
      }
      return { state: 'hasData', data: value.data };
    },
    (get, set) => {
      set(refreshFamily(key), get(refreshFamily(key)) + 1);
    },
  );
});

const idleCountsAtom = atom(
  (): CountState => ({ state: 'idle' }),
  () => undefined,
);

const toResult = (
  result: CountState,
  execute: () => void,
): MemberExtractionReviewCountsResult => ({
  data: result.state === 'hasData' ? result.data : undefined,
  error: result.state === 'hasError' ? result.error : undefined,
  isFetching: result.state === 'loading',
  isIdle: result.state === 'idle',
  execute,
});

export const useMemberExtractionReviewCounts = (
  patientId: MaybeSource<string | null | undefined>,
  options: AsyncOptions = {},
): MemberExtractionReviewCountsResult => {
  const immediate = options.immediate !== false;
  const [started, setStarted] = React.useState(immediate);
  const auth = useServiceAuth();
  const projectId = useActiveProjectId();
  const resolvedPatientId = resolveSource(patientId);
  const setSearchAuth = useSetAtom(searchAuthAtom);

  React.useEffect(() => {
    setSearchAuth(auth);
  }, [auth, setSearchAuth]);

  const canRun = started && Boolean(projectId) && Boolean(resolvedPatientId);
  const [result, refresh] = useAtom(
    canRun
      ? countsFamily(countKey(projectId, resolvedPatientId as string))
      : idleCountsAtom,
  );

  const execute = () => {
    if (!started) {
      setStarted(true);
      return;
    }
    refresh();
  };

  return toResult(result, execute);
};
